<template>
  <div class="w-full page-content">
    <h1 class="visually-hidden">{{ seoTitleH1 }}</h1>
    <BlocksContainer
      :components="pageData?.blocks || []"
      :page-data="pageData"
    />
  </div>
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { $api } = useNuxtApp();
const route = useRoute();
const slug = route.path.slice(1);
const generalStore = useGeneralStore();
const isMounted = useMounted();

const config = useRuntimeConfig();
const i18nHead = useLocaleHead({
  addSeoAttributes: {
    canonicalQueries: ["page"],
  },
});
const { setBreadcrumbs, findBreadcrumb } = useBreadcrumbs();
const { setPageJsonLdSchema } = useJsonLdSchema();

const { headerMenu, getFilter } = useGeneralStore();

const { visitEvent, viewSearchResultEvent, dynamicParamsEvent } = useEvents();

const { data } = await useAsyncCachedData(`page-${slug}`, () => {
  return $api.pages.getPage(slug);
});

const pageData = data.value?.data;

const isCatalogPage = computed(() => {
  return Boolean(
    pageData?.blocks?.some((item) => item?.type?.identifier === "catalog"),
  );
});

if (isCatalogPage.value) {
  await getFilter();
}
const { setNewBreadcrumbs } = useFullBreadcrumbs();

setNewBreadcrumbs();

setJSONLdSchema();

useHead(() => ({
  meta: [
    ...metaGenerator({
      title:
        pageData?.currentTranslation?.meta_title ||
        pageData?.currentTranslation?.title ||
        "",
      description: pageData?.currentTranslation?.meta_description || "",
      image:
        getImageByPosition(pageData?.medias || [], "open-graph")?.file?.url ||
        "",
      url: pageData?.currentTranslation?.meta_url || config.public.domain,
    }),
    ...(i18nHead.value.meta || []),
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
}));

const plpPaginationPage = computed(() => {
  return route.query.page || (isCatalogPage.value ? 1 : null);
});

const { seoTitle, seoTitleH1, seoDescription } = usePageSeo();

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    pageData?.currentTranslation?.meta_description || seoDescription.value,
  ogDescription: () =>
    pageData?.currentTranslation?.meta_description || seoDescription.value,
});

watchEffect(() => {
  if (isMounted.value) {
    if (route.query.search) {
      viewSearchResultEvent(route.query.search as string);
    }
    dynamicParamsEvent("View Other", {
      ecomm_prodid: "",
      ecomm_pagetype: "other",
      ecomm_totalvalue: "",
      ecomm_category: "",
    });
  }
});

onMounted(() => {
  visitEvent(pageData?.is_plp);
});

function usePageSeo() {
  const seoTitlePart = computed(() => {
    const seoFilterName = generalStore.seoFilterName(route.query);
    let title =
      pageData?.currentTranslation?.meta_title ||
      pageData?.currentTranslation?.title ||
      "";
    if (seoFilterName) {
      title = `${seoFilterName} ${title}`;
    }
    if (plpPaginationPage.value) {
      title = `${title}: ${t("page")} №${plpPaginationPage.value}`;
    }
    return title;
  });

  const seoTitle = computed(() => {
    let title = seoTitlePart.value;
    if (generalStore.settings?.company_name) {
      title = `${title} | ${generalStore.settings.company_name}`;
    }
    return title;
  });

  const seoTitleH1 = computed(() => seoTitlePart.value);

  const seoDescription = computed(() => {
    return isCatalogPage.value
      ? `${t("explore")} ${seoTitlePart.value} ${t("metaDescription")}`
      : `${seoTitlePart.value} ${t("pageMetaDescription")}`;
  });
  return {
    seoTitle,
    seoTitleH1,
    seoDescription,
  };
}
function setJSONLdSchema() {
  const name = pageData?.currentTranslation?.title || "";
  const image =
    getImageByPosition(pageData?.medias || [], "open-graph")?.file?.url ||
    getImageByPosition(pageData?.medias || [], "page")?.file?.url ||
    "";

  if (!isCatalogPage.value) {
    setPageJsonLdSchema({
      name: name || "",
      image,
    });
  }
}

function useFullBreadcrumbs() {
  const navigation = computed(() => {
    return toTree(headerMenu);
  });

  const fullBreadcrumbs = computed(() =>
    findBreadcrumb(navigation.value, route),
  );

  function setNewBreadcrumbs() {
    setBreadcrumbs(
      fullBreadcrumbs.value.length
        ? [
            { text: t("home"), href: "/", disabled: false },
            ...fullBreadcrumbs.value,
          ]
        : [
            { text: t("home"), href: "/", disabled: false },
            {
              text: pageData?.currentTranslation?.title || "",
              href: `/${pageData?.slug}`,
              disabled: true,
            },
          ],
    );
  }
  return { setNewBreadcrumbs };
}
</script>
